/**
 * The farrowing hall — the first room in the game, and M0b's dressed room.
 *
 * Spec §7 beats 1 and 2 happen here, so this is shipping content rather than a
 * demo: *Born* and *The Weakest Latch*. Everything about it is downstream of one
 * sentence in `docs/art-direction.md` — the farm is hard, orthogonal, repetitive
 * and cold, and she is the only curve in it.
 *
 * ── Staging ───────────────────────────────────────────────────────────────────
 * Composition comes from `docs/verification/moodboard/plates/1-farrowing.png`:
 * a long hall with a central walkway, an open drain running down it, crate rows
 * either side, and the far end dissolving into haze. The perspective convergence
 * IS the staging — it is what makes the space read as endless and as built for
 * somebody else. That plate is a composition reference and explicitly not a
 * rendering reference (D-008); the look comes from `palette.ts` and the shader.
 *
 * ── Why the hall is 20 m and not 60 ───────────────────────────────────────────
 * "Endless" is delivered by the fog, not by geometry. At the farrowing zone's
 * haze the far wall is already dissolving at about 18 m, so anything past that
 * is triangles nobody will ever resolve. If the hall ever needs to feel longer,
 * raise `haze` in the palette before adding metres.
 *
 * ── Scale is the argument ─────────────────────────────────────────────────────
 * Every human dimension here is a real one: 2.9 m ceiling, 1.05 m door handle,
 * 0.55 m kerb. She is 0.32 m tall (D-012). Nothing is shrunk to be convenient,
 * because the mismatch is the point and it costs nothing to keep.
 */

import * as THREE from 'three';
import { disposeAsset, surface } from '@/models/materials';
import { getAsset } from '@/models/registry';
import { getZone, type ZonePalette } from '@/render/palette';
import { collapseRigged, instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';
import type { Fixture } from '@/render/lighting';
import { penGateLeaf } from '@/models/env/pen-gate';
import { personnelDoorAsset } from '@/models/env/personnel-door';
import { penRailSpan } from '@/models/env/pen-rail';
import { FARROWING_CRATE_LAYOUT, farrowingCrateGateAccess } from '@/models/env/farrowing-crate';

/** The zone this room is in. Everything else reads its palette from here. */
export const FARROWING_ZONE_ID = 'farrowing';

/** Interior dimensions, metres. The hall runs along Z. */
export const HALL = {
  halfWidth: 3.6,
  halfLength: 10,
  ceiling: 2.9,
  /** Walkway half-width. Wide enough for a person and a hose, and no wider. */
  walkway: 0.7,
  /**
   * The open drain down the middle of the walkway.
   *
   * **It was a trap and this is the fix.** Reported at review: she goes down the
   * gutter and cannot get back up, by autostep or by rolling.
   *
   * The cause was not autostep failing on a small ledge — it manages 0.16 m in
   * the test room. It is that **a groove with vertical walls captures her
   * heading.** Movement is camera-relative, the camera follows her facing, and
   * she turns to face the way she is moving; so a push at a wall becomes a slide
   * ALONG the wall, which turns her, which swings the camera, which turns
   * "sideways" into "along the channel". She ends up trotting the length of the
   * sluice every time, whatever the player does, because she never pushes at the
   * wall for more than a frame.
   *
   * Two changes, and the measurements that chose them:
   *
   * - **Sloped lips instead of vertical walls** (`drainChamfer`). Truer to a cast
   *     concrete sluice, which is formed with sloped sides so it can be hosed
   *     out. On its own this was **not enough**: at 0.10 m deep she could rest
   *     anywhere on the slope but still could not walk up it, because the
   *     heading loop kept converting her push into a run along the channel.
   * - **Shallow.** Measured escape, holding forward while steering:
   *     0.10 m → never; 0.07 m → out in ~2 s; **0.05 m → out in ~0.7 s.**
   *
   * A player who holds forward and never touches the mouse still runs along the
   * channel rather than out of it. That is left alone deliberately: it is what a
   * groove should do, it takes one flick of the mouse to leave, and at 0.05 m
   * she is barely in it.
   *
   * **This is not the finished object.** A real farrowing sluice is grated over,
   * which would remove the hazard entirely, keep the dark line that makes the
   * walkway read, and give `foley.ts`'s already-written grating footfall — the
   * loudest surface in the emission ladder — its first use. That is M1 work; see
   * STATUS.
   */
  drainHalfWidth: 0.18,
  drainDepth: 0.05,
  /**
   * The sloped lip either side of the channel, metres of horizontal run.
   *
   * 0.16 m of run against 0.05 m of rise is 17°, far inside
   * `maxSlopeClimbAngle` (37.8°) — and the shallowness is doing most of the work
   * here, not the angle. See the note above.
   */
  drainChamfer: 0.16,
  wallThickness: 0.2,
} as const;

/**
 * The walkway's doorway in the near end wall, and the vestibule behind it: the
 * only way out of the hall. Beat 1's worker comes and goes through it, and
 * beat 2 ends with her squeezing through it ajar.
 *
 * Until session 70 the exit was a hall door at x = −1.9, standing in the end
 * wall at the back of HER OWN PEN, in front of an unbroken wall collider, so
 * the exit flag was true on the first tick (`docs/playthrough-audit.md` rows 2
 * and 3). Until session 94 that false door stayed dressed there, with a strip
 * curtain in front of it that both beats' workers walked through: through the
 * wall, and diagonally through her pen (`docs/beat-1-revision-plan.md`, D).
 * Both are gone. `walkway-door.spec.ts` walks both workers' routes against
 * every solid in the hall.
 *
 * `halfWidth` is the `personnel-door` frame's own half-width, so the wall
 * meets the frame. It was 0.55 when the doorway was empty, which left a 4 cm
 * slit down each jamb once a 1.02 m frame stood in it.
 *
 * The vestibule is bounded (D-089): a doorway with nothing behind it is a hole
 * in the world, and `room-containment.spec.ts`'s south escape walks straight
 * into it. It is also where both workers park (`BOOTS_PARK`), which is why it
 * is 1.6 m deep and not the 1.2 it was: the leaf swings 0.9 m into it, and a
 * man 0.32 m deep has to stand behind that swing with his back off the wall.
 */
export const WALKWAY_DOOR = {
  halfWidth: personnelDoorAsset.footprint.width / 2,
  height: 2.1,
  vestibuleHalfX: 0.75,
  vestibuleDepth: 1.6,
} as const;

/**
 * Past this z she is through the walkway doorway and out of the hall — the
 * wall's outer face. A plane she can only cross by walking through the door,
 * not a radius around a point (which is how the old target sat inside the pen).
 */
export const WALKWAY_DOOR_OUT_Z = -HALL.halfLength - HALL.wallThickness;

/**
 * How self-lit the vestibule's floor and walls are (`SurfaceOptions.glow`):
 * the light behind the ajar walkway door that draws her to it in beat 2.
 *
 * Tuned on `door-frames.spec.ts`'s frame from 5 m down the walkway at her eye
 * height (`DOORSHOT=1`). Probed at 5 first: pure white, so the term was wired.
 * At 0.8 the gap was pale but sank into the end wall. At 1.5 the gap's mean
 * luminance is 177 against 108 on the leaf beside it and 120 on the end wall,
 * on a 0–255 scale. It lights nothing: it is not a fixture, so the stealth
 * light grid does not see it and she is not lit by it.
 */
const VESTIBULE_GLOW = 1.5;

/**
 * Where the door asset stands: centred in the doorway, in the middle of the
 * wall's thickness. Unrotated, so its push bar faces the hall, and its leaf
 * swings into the vestibule (positive angles), away from the walkway she
 * comes down.
 */
export const WALKWAY_DOOR_AT = { x: 0, z: -HALL.halfLength - HALL.wallThickness / 2 } as const;

/** How far the leaf swings to let a man through: a right angle, flat against nothing. */
export const WALKWAY_DOOR_OPEN = Math.PI / 2;

/**
 * How far it stands open once beat 2's worker has come through it: a gap a
 * piglet gets through and a man does not. 0.45 rad leaves 0.31 m between the
 * leaf and the far jamb: her 0.25 m with room to turn, and less than the
 * 0.36 m the `worker` is front to back. `walkway-door.spec.ts` measures the
 * gap and floods it.
 */
export const WALKWAY_DOOR_AJAR = 0.45;

/**
 * The door's shape as the asset builds it: the hinge, the leaf panel in the
 * hinge's own frame, and the two frame jambs, all world space at
 * `WALKWAY_DOOR_AT`. Measured from `personnel-door` rather than restated,
 * because three numbers copied out of an asset are three numbers that stop
 * agreeing with it the next time somebody touches the door.
 *
 * The leaf box is the panel alone. The push bar stands 8 cm proud on the hall
 * side at 1.05 m and the kick plate 1 cm: neither is at her height, and a
 * collider that included them would be a door that stops her short of itself.
 */
interface WalkwayDoorShape {
  hinge: { x: number; z: number };
  leaf: { cx: number; cy: number; cz: number; hx: number; hy: number; hz: number };
  jambs: { name: string; min: THREE.Vector3; max: THREE.Vector3 }[];
}

let walkwayDoorShapeCache: WalkwayDoorShape | null = null;

function walkwayDoorShape(): WalkwayDoorShape {
  if (walkwayDoorShapeCache !== null) return walkwayDoorShapeCache;
  const door = personnelDoorAsset.create();
  door.position.set(WALKWAY_DOOR_AT.x, 0, WALKWAY_DOOR_AT.z);
  door.updateMatrixWorld(true);
  const hinge = door.getObjectByName('door-hinge');
  const panel = door.getObjectByName('steel-leaf-panel') as THREE.Mesh | undefined;
  if (!hinge || !panel) throw new Error('walkway door: the personnel-door asset has no rigged leaf');
  const hingeAt = new THREE.Vector3().setFromMatrixPosition(hinge.matrixWorld);
  panel.geometry.computeBoundingBox();
  const local = panel.geometry.boundingBox?.clone().applyMatrix4(panel.matrix);
  if (!local) throw new Error('walkway door: the leaf panel has no bounds');
  const centre = local.getCenter(new THREE.Vector3());
  const size = local.getSize(new THREE.Vector3());
  const jambs = (['frame-left', 'frame-right'] as const).map((name) => {
    const mesh = door.getObjectByName(name);
    if (!mesh) throw new Error(`walkway door: the personnel-door asset has no ${name}`);
    const box = new THREE.Box3().setFromObject(mesh);
    return { name, min: box.min, max: box.max };
  });
  disposeAsset(door);
  walkwayDoorShapeCache = {
    hinge: { x: hingeAt.x, z: hingeAt.z },
    // The hinge sits on the floor, so the leaf's local y is its world y.
    leaf: { cx: centre.x, cy: centre.y + hingeAt.y, cz: centre.z, hx: size.x / 2, hy: size.y / 2, hz: size.z / 2 },
    jambs,
  };
  return walkwayDoorShapeCache;
}

/** The hinge, world XZ. */
export function walkwayDoorHinge(): { x: number; z: number } {
  return { ...walkwayDoorShape().hinge };
}

/**
 * The leaf as a collider at `angle` (0 shut, positive into the vestibule).
 *
 * Same rotation as the mesh's hinge, `rotation.y = angle`, which maps the
 * hinge's local (x, z) to world (x cos + z sin, −x sin + z cos). The leaf's
 * centre orbits the hinge, which is why this is a function and not a yaw on a
 * fixed box: see `penGateLeaf`, which learned it first.
 */
export function walkwayDoorLeaf(angle: number): HallSolid {
  const { hinge, leaf } = walkwayDoorShape();
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return {
    name: 'walkway-door-leaf',
    half: { x: leaf.hx, y: leaf.hy, z: leaf.hz },
    pos: { x: hinge.x + leaf.cx * c + leaf.cz * s, y: leaf.cy, z: hinge.z - leaf.cx * s + leaf.cz * c },
    yaw: angle,
    colour: 0,
    kind: 'metal',
    visible: false,
  };
}

/**
 * Whether the leaf at `angle` overlaps a circle in plan: her capsule, seen
 * from above. Heights are not asked: the leaf runs from the floor to 2.05 m,
 * so anything standing on the floor near it is beside it at some height.
 */
export function walkwayDoorLeafTouches(angle: number, at: { x: number; z: number }, radius: number): boolean {
  const leaf = walkwayDoorLeaf(angle);
  const dx = at.x - leaf.pos.x;
  const dz = at.z - leaf.pos.z;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  // Into the leaf's own frame: the inverse of `rotation.y = angle`.
  const lx = dx * c - dz * s;
  const lz = dx * s + dz * c;
  const ox = Math.max(Math.abs(lx) - leaf.half.x, 0);
  const oz = Math.max(Math.abs(lz) - leaf.half.z, 0);
  return Math.hypot(ox, oz) < radius;
}

/**
 * The door's solids: the two frame jambs, and the leaf SHUT.
 *
 * The leaf carries `role: 'walkway-door'` so beat 2 can swap it for the one
 * it swings; every other beat gets a shut door, which is what
 * `walkway-door.spec.ts`'s first test floods. Invisible, because the door
 * asset draws all three.
 */
export function walkwayDoorSolids(): HallSolid[] {
  const { jambs } = walkwayDoorShape();
  return [
    ...jambs.map((j): HallSolid => ({
      name: `walkway-door-${j.name}`,
      half: { x: (j.max.x - j.min.x) / 2, y: (j.max.y - j.min.y) / 2, z: (j.max.z - j.min.z) / 2 },
      pos: { x: (j.max.x + j.min.x) / 2, y: (j.max.y + j.min.y) / 2, z: (j.max.z + j.min.z) / 2 },
      colour: 0,
      kind: 'metal',
      visible: false,
    })),
    { ...walkwayDoorLeaf(0), role: 'walkway-door' },
  ];
}

/**
 * The door itself, live: merged per material either side of its hinge, so the
 * frame is one draw and the leaf is one more, and the hinge still turns.
 * `createFarrowingHall` places it; the beats turn `door-hinge`.
 */
function createWalkwayDoor(): THREE.Group {
  const door = personnelDoorAsset.create();
  door.name = 'walkway-door';
  const hinge = door.getObjectByName('door-hinge');
  if (!hinge) throw new Error('walkway door: the personnel-door asset has no hinge');
  collapseRigged(door, [hinge]);
  door.position.set(WALKWAY_DOOR_AT.x, 0, WALKWAY_DOOR_AT.z);
  door.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.castShadow = true;
      m.receiveShadow = true;
    }
  });
  return door;
}

/**
 * Where she starts: near end of the walkway, beside the drain rather than on it.
 *
 * Off-centre on purpose. Spawning at x = 0 put her straddling the channel, and
 * the drain also happens to be the strongest leading line in every shot down the
 * hall — she reads better beside it than standing in it.
 */
export const FARROWING_SPAWN = { x: 0.45, y: 0.25, z: -8.4 } as const;

/**
 * Bay pitch along Z. Every repeated kit piece is placed on this.
 *
 * 2.4 m is a real farrowing bay: a 2.1 m crate plus working clearance. Keeping
 * the module honest is what lets the room be extended later without any of it
 * needing to be re-authored.
 */
export const BAY_PITCH = 2.4;

/** One intentional break in the kerb: Beat 2's first route out of the birth pen. */
export interface PenGateMouth {
  side: -1 | 1;
  z: number;
  width: number;
  swing: -1 | 1;
}

// `bornBay.ts` imports this module, so importing its exported BAY_Z would make
// the hall and the beat initialise each other. This is the same declared bay
// centre, kept here as layout data until a zone contract owns both modules.
export function penGateMouths(): PenGateMouth[] {
  return [{
    side: -1,
    z: -HALL.halfLength + BAY_PITCH / 2,
    width: 1,
    swing: 1,
  }];
}

/**
 * Thickness of a slat-floor tile, metres — the height the pen deck sits at.
 *
 * Real slats are laid over a slurry channel, so the pen floor is genuinely a
 * step up from the walkway. Anything placed in a pen has to know this or it
 * sinks into the deck.
 */
const SLAT_HEIGHT = 0.09;

/** Z centres of the ceiling battens. The lights and the fittings share them. */
const BATTEN_Z: readonly number[] = [-8, -4, 0, 4, 8];

/**
 * The pen rail's collision span. See where it is pushed, in `farrowingSolids`.
 *
 * The top matches `pen-rail.ts`'s own top rail at 1.03. The bottom is 0.05 m
 * below the 0.55 m kerb top on purpose, so the two overlap rather than abut.
 */
const PEN_RAIL_TOP_Y = 1.03;
const PEN_RAIL_BOTTOM_Y = 0.5;
/** Buries the crate-end collision into the raised slat deck, sealing its base. */
const OUTBOARD_RAIL_DECK_BITE = 0.06;
/**
 * The rail assembly's collision depth, including the deck it terminates in.
 *
 * A pipe-width box lets the 0.35 m scramble landing ray land beyond its outer
 * face, even while the standing capsule is blocked. This span begins at the
 * drawn rail's player-facing line and extends into the inaccessible exterior
 * strip, so the rail and deck are one closed collision boundary rather than a
 * traversable ledge at their join.
 */
const OUTBOARD_RAIL_HALF_DEPTH = 0.2;

// `HallSolid` moved to `solids.ts` when the fattening hall needed it too, and is
// re-exported here so every existing importer keeps working. See that file for
// why one box is both the mesh and the collider.
export type { HallSolid };

// Resolved in an IIFE rather than as `const z = getZone(...); if (!z) throw`,
// because TypeScript does not carry that narrowing into the function bodies
// below — a module-level guard reads as proof to a human and as nothing at all
// to the checker.
const zone: ZonePalette = (() => {
  const found = getZone(FARROWING_ZONE_ID);
  if (!found) throw new Error(`farrowing zone missing from the palette`);
  return found;
})();

// Albedo, straight from the zone's own surface list. The values are ordered
// darkest to lightest in palette.ts, and using them by index rather than by
// fresh hex literals is what keeps this room inside its zone when the palette
// is next tuned.
const CONCRETE_FLOOR = zone.surfaces[1] ?? 0x6b716e;
const CONCRETE_WALL = zone.surfaces[2] ?? 0x8d918c;
const CONCRETE_DARK = zone.surfaces[0] ?? 0x39433f;
const STEEL = zone.surfaces[4] ?? 0xa8aeb2;

/**
 * Every box in the hall shell, mesh and collider alike.
 *
 * One list for both, exactly as the grey-box room does it — a room whose
 * collision does not match what you can see is not a room, it is a source of
 * phantom bugs, and "she clips the wall" becomes indistinguishable from a
 * controller defect.
 */
export function farrowingSolids(): HallSolid[] {
  const solids: HallSolid[] = [];
  const { halfWidth, halfLength, ceiling, drainHalfWidth, drainDepth, wallThickness } = HALL;
  /** Where the flat walkway starts, outboard of the channel and its sloped lip. */
  const lipOuter = drainHalfWidth + HALL.drainChamfer;

  // The floor is TWO slabs with a gap, not one slab with a hole. Rapier cuboids
  // are convex, so a recess has to be built out of the space around it — and
  // doing that in the collider but not the mesh (or the reverse) is how a
  // character ends up walking on air over a drain she can visibly see.
  const floorHalfX = (halfWidth - lipOuter) / 2;
  // Thick enough that the slab's UNDERSIDE is below the drain bed's top face.
  // At the first thickness (0.05 half) the two stopped 20 mm short of meeting,
  // leaving a slot down both sides of the channel that you could see the void
  // through — the kind of hole that is invisible from standing height and
  // glaring from hers, which is the only height this game is ever seen from.
  const floorHalfY = drainDepth;
  for (const side of [-1, 1] as const) {
    solids.push({
      name: `floor-${side > 0 ? 'r' : 'l'}`,
      half: { x: floorHalfX, y: floorHalfY, z: halfLength },
      pos: { x: side * (lipOuter + floorHalfX), y: -floorHalfY, z: 0 },
      colour: CONCRETE_FLOOR,
      kind: 'mineral',
    });
  }

  // The drain's own floor, one channel-depth lower.
  solids.push({
    name: 'drain-bed',
    half: { x: drainHalfWidth, y: 0.05, z: halfLength },
    pos: { x: 0, y: -drainDepth - 0.05, z: 0 },
    colour: CONCRETE_DARK,
    kind: 'mineral',
  });

  // Side walls.
  for (const side of [-1, 1] as const) {
    solids.push({
      name: `wall-${side > 0 ? 'r' : 'l'}`,
      half: { x: wallThickness / 2, y: ceiling / 2, z: halfLength },
      pos: { x: side * (halfWidth + wallThickness / 2), y: ceiling / 2, z: 0 },
      colour: CONCRETE_WALL,
      kind: 'mineral',
    });
  }

  // End walls. The far one is the vanishing point of every shot down the hall,
  // so it is deliberately the darkest surface in the room: the eye is meant to
  // travel to it and find nothing.
  solids.push({
    name: 'end-far',
    half: { x: halfWidth + wallThickness, y: ceiling / 2, z: wallThickness / 2 },
    pos: { x: 0, y: ceiling / 2, z: halfLength + wallThickness / 2 },
    colour: CONCRETE_DARK,
    kind: 'mineral',
  });
  // The near one in three pieces round the walkway door. See `WALKWAY_DOOR`.
  const nearZ = -halfLength - wallThickness / 2;
  const outerX = halfWidth + wallThickness;
  const door = WALKWAY_DOOR;
  for (const side of [-1, 1] as const) {
    solids.push({
      name: `end-near-${side > 0 ? 'r' : 'l'}`,
      half: { x: (outerX - door.halfWidth) / 2, y: ceiling / 2, z: wallThickness / 2 },
      pos: { x: side * (outerX + door.halfWidth) / 2, y: ceiling / 2, z: nearZ },
      colour: CONCRETE_WALL,
      kind: 'mineral',
    });
  }
  solids.push({
    name: 'end-near-lintel',
    half: { x: door.halfWidth, y: (ceiling - door.height) / 2, z: wallThickness / 2 },
    pos: { x: 0, y: (ceiling + door.height) / 2, z: nearZ },
    colour: CONCRETE_WALL,
    kind: 'mineral',
  });

  // The vestibule: shut, exactly deep enough to walk into, and LIT. Its
  // floor and walls are self-lit (`VESTIBULE_GLOW`), so the gap the worker
  // leaves in beat 2 is the brightest line at the end of the hall. The
  // ceiling is not: a lit room seen through a door shows its floor and walls.
  const vestibuleMinZ = -halfLength - wallThickness - door.vestibuleDepth;
  const vestibuleCentreZ = (vestibuleMinZ - halfLength - wallThickness) / 2;
  const vestibuleHalfZ = door.vestibuleDepth / 2;
  solids.push(
    {
      name: 'vestibule-floor',
      half: { x: door.vestibuleHalfX, y: 0.05, z: vestibuleHalfZ + wallThickness / 2 },
      pos: { x: 0, y: -0.05, z: vestibuleCentreZ + wallThickness / 4 },
      colour: CONCRETE_FLOOR,
      kind: 'mineral',
      glow: VESTIBULE_GLOW,
    },
    {
      name: 'vestibule-ceiling',
      half: { x: door.vestibuleHalfX + wallThickness, y: 0.05, z: vestibuleHalfZ + wallThickness },
      pos: { x: 0, y: ceiling + 0.05, z: vestibuleCentreZ },
      colour: CONCRETE_DARK,
      kind: 'mineral',
    },
    {
      name: 'vestibule-back',
      half: { x: door.vestibuleHalfX + wallThickness, y: ceiling / 2, z: wallThickness / 2 },
      pos: { x: 0, y: ceiling / 2, z: vestibuleMinZ - wallThickness / 2 },
      colour: CONCRETE_WALL,
      kind: 'mineral',
      glow: VESTIBULE_GLOW,
    },
  );
  for (const side of [-1, 1] as const) {
    solids.push({
      name: `vestibule-wall-${side > 0 ? 'r' : 'l'}`,
      half: { x: wallThickness / 2, y: ceiling / 2, z: vestibuleHalfZ },
      pos: { x: side * (door.vestibuleHalfX + wallThickness / 2), y: ceiling / 2, z: vestibuleCentreZ },
      colour: CONCRETE_WALL,
      kind: 'mineral',
      glow: VESTIBULE_GLOW,
    });
  }
  // And the door that stands in it: shut, until a beat swings it.
  solids.push(...walkwayDoorSolids());

  // Ceiling. Darker than the walls, which is both true of a real hall and the
  // cheapest way to keep the eye down at her height where the game happens.
  solids.push({
    name: 'ceiling',
    half: { x: halfWidth + wallThickness, y: 0.05, z: halfLength },
    pos: { x: 0, y: ceiling + 0.05, z: 0 },
    colour: CONCRETE_DARK,
    kind: 'mineral',
  });

  // Kerbs: the low concrete wall between the walkway and each pen row. This is
  // the first piece of architecture in the game she cannot simply walk over —
  // 0.55 m against her 0.32 m — and it is doing that work before any system
  // exists to enforce it.
  // ONE kerb per side, running the whole hall — minus its declared gate mouths.
  //
  // It was sixteen bay-length segments with 20 mm gaps between them, which is
  // how a real one is cast and cost fourteen extra meshes for a joint nobody can
  // see — and every mesh here is paid for three times over: shadow pass, normal
  // prepass, beauty pass. Detail that survives none of those passes is not
  // detail, it is budget. **That is still the rule**, and it is why the segments
  // below are generated from `penGateMouths()` rather than cut by hand for one
  // gate: a mouth is a real absence in the barrier and earns its mesh, and a
  // joint is not.
  //
  // The kerb is a *visible* solid, so cutting the mouth here removes the
  // concrete and the collision in one edit. That is the whole reason the room is
  // built from one list.
  for (const side of [-1, 1] as const) {
    const mouths = penGateMouths().filter((mouth) => mouth.side === side)
      .map((mouth) => ({ from: mouth.z - mouth.width / 2, to: mouth.z + mouth.width / 2 }))
      .sort((a, b) => a.from - b.from);
    let from = -halfLength;
    let segment = 0;
    for (const mouth of [...mouths, { from: halfLength, to: halfLength }]) {
      if (mouth.from > from) {
        const to = mouth.from;
        solids.push({
          name: `kerb-${side > 0 ? 'r' : 'l'}-${segment}`,
          half: { x: 0.06, y: 0.275, z: (to - from) / 2 },
          pos: { x: side * (HALL.walkway + 0.06), y: 0.275, z: (from + to) / 2 },
          colour: CONCRETE_WALL,
          kind: 'mineral',
        });
        segment += 1;
      }
      from = Math.max(from, mouth.to);
    }
  }

  // The pen rail, which has been DRAWN along every bay since the hall was
  // dressed and has never been solid.
  //
  // ── R-023: this is how she got out of beat 1's pen, and why it was fatal ─────
  //
  // `pen-rail.ts` calls itself "a galvanized guard frame that continues the pen
  // kerb upward"; `dressFarrowingHall` instances one per bay per side, standing
  // on this exact line and reaching 1.03 m. A player could walk through it.
  //
  // The kerb alone is a 0.55 m barrier, which refuses her at 0.32 m tall — from
  // the FLOOR. It does not refuse her from anything she can stand on, and the
  // pen is full of things she can stand on: a sleeping littermate tops out at
  // 0.41, the crate's creep kerb at 0.35, the bay divider at 0.65. From the
  // creep kerb the top of the concrete is 0.22 m up, which is exactly
  // `scrambleMinHeight` — so she scrambles onto the kerb and walks off it into
  // the walkway. And from the walkway there is no way back: 0.55 m of concrete
  // against a 0.34 m scramble, measured at nine points beside the bay with
  // `peakY` never leaving 0.000. Beat 1 happens entirely inside that pen.
  //
  // So the fix is not to hunt the next platform. It is to make the barrier the
  // player can already SEE be the barrier that is there, along the whole run of
  // it — the kerb top stops being a walkable ledge anywhere in the hall.
  //
  // Only the span above the kerb is given collision, and it starts 0.05 m INSIDE
  // the concrete rather than on its top face: two boxes that share a face share
  // a zero-width seam the solver can separate through, which is the same lesson
  // the floor slabs above learned from the other direction. Nothing at floor
  // level changes on either side — her head is at 0.32 on the walkway and 0.41
  // on a pen deck, both under the 0.50 underside.
  for (const side of [-1, 1] as const) {
    const mouths = penGateMouths().filter((mouth) => mouth.side === side)
      .map((mouth) => ({ from: mouth.z - mouth.width / 2, to: mouth.z + mouth.width / 2 }))
      .sort((a, b) => a.from - b.from);
    let from = -halfLength;
    let segment = 0;
    for (const mouth of [...mouths, { from: halfLength, to: halfLength }]) {
      if (mouth.from > from) {
        const to = mouth.from;
        solids.push({
          name: `pen-rail-solid-${side > 0 ? 'r' : 'l'}-${segment}`,
          half: { x: 0.06, y: (PEN_RAIL_TOP_Y - PEN_RAIL_BOTTOM_Y) / 2, z: (to - from) / 2 },
          pos: {
            x: side * (HALL.walkway + 0.06),
            y: (PEN_RAIL_TOP_Y + PEN_RAIL_BOTTOM_Y) / 2,
            z: (from + to) / 2,
          },
          colour: STEEL,
          kind: 'metal',
          visible: false,
          // The follow lens rides inside this 1.03 m frame; see SWEEP_GROUPS in
          // `main.ts` for the arm collapse this prevents.
          camera: 'passes',
        });
        segment += 1;
      }
      from = Math.max(from, mouth.to);
    }
  }

  // Every crate visibly closes its outboard end with low creep kerbs and the
  // sow lane's tall end rails, yet those rails used to have no collider. That
  // let Rosalie leave the birth bay through the wall-side slat strip. A collider
  // only on Beat 1's crate reduced the route but did not close it: she could
  // climb a low divider into the neighbouring bay and use its identical naked
  // rail. The repeated kit therefore gets repeated collision here, where its
  // visible placements are declared, rather than as a one-off born-bay patch.
  //
  // The slab bites into the deck below and puts its player-facing face on the
  // rail centreline; its thickness is all exterior-side, never extra invisible
  // blockage on the accessible deck. Its
  // 0.38 m end bite overlaps the two adjacent drawn bay partitions: the rail's
  // physical 0.90 m half-span stopped short of their centreline, and attempts
  // at z = −9.835 / −7.765 rounded those exposed ends into the wall-side strip.
  // It is a simplified collision volume for an assembly of pipes, as the
  // crate's lane partitions are in `bornBaySolids()`: treating each pipe
  // separately creates gaps no piglet can see but a capsule can climb through.
  for (const side of [-1, 1] as const) {
    for (const [index, z] of bayCentres().entries()) {
      solids.push({
        name: `crate-outboard-solid-${side > 0 ? 'r' : 'l'}-${index}`,
        half: {
          x: OUTBOARD_RAIL_HALF_DEPTH,
          y: (FARROWING_CRATE_LAYOUT.outerEndRailTop + OUTBOARD_RAIL_DECK_BITE) / 2,
          z: BAY_PITCH / 2 + 0.08,
        },
        pos: {
          x: side * (
            HALL.walkway + 0.12 + 1.15 + FARROWING_CRATE_LAYOUT.halfLength + OUTBOARD_RAIL_HALF_DEPTH
          ),
          y: SLAT_HEIGHT + (FARROWING_CRATE_LAYOUT.outerEndRailTop - OUTBOARD_RAIL_DECK_BITE) / 2,
          z,
        },
        colour: STEEL,
        kind: 'metal',
        visible: false,
      });
    }
  }

  for (const [index, mouth] of penGateMouths().entries()) {
    const hinge = {
      x: mouth.side * (HALL.walkway + 0.06),
      z: mouth.z + mouth.swing * mouth.width / 2,
    };
    const leaf = penGateLeaf(hinge, mouth.swing, 0);
    // The leaf is authored along local +X, while its shut hall pose is a quarter
    // turn. Keep the collider derived from that pose; when Claude opens this
    // gate, the angle needs a rotated collider too, not a stale shut cuboid.
    solids.push({
      name: `pen-gate-solid-${mouth.side > 0 ? 'r' : 'l'}-${index}`,
      half: { x: leaf.half.z, y: leaf.half.y, z: leaf.half.x },
      pos: leaf.pos,
      colour: STEEL,
      kind: 'metal',
      visible: false,
      role: 'shut-pen-gate',
    });
  }

  for (const [index, { side, z }] of penPartitionLines().entries()) {
    solids.push({
      name: `pen-partition-${side > 0 ? 'r' : 'l'}-${index}`,
      half: { x: 1.58, y: 0.325, z: 0.03 },
      pos: { x: side * 2.34, y: 0.325, z },
      colour: STEEL,
      kind: 'metal',
      visible: false,
    });
  }

  // A service pipe run at head height along both walls. Pure dressing, and it is
  // load-bearing dressing: a bare wall at this scale reads as an untextured box
  // no matter how well it is shaded.
  for (const side of [-1, 1] as const) {
    solids.push({
      name: `pipe-${side > 0 ? 'r' : 'l'}`,
      half: { x: 0.035, y: 0.035, z: halfLength },
      pos: { x: side * (halfWidth - 0.12), y: 2.15, z: 0 },
      colour: STEEL,
      kind: 'metal',
    });
  }

  return solids;
}

/**
 * The practical lights: fluorescent battens down the centre of the ceiling.
 *
 * Spaced 4 m apart, which is further than a real hall would put them. That gap
 * is the point — it makes the walkway a rhythm of pools and gaps rather than an
 * evenly lit corridor, and the gaps are where the stealth system will let her
 * live. Spec §5.1: dark is safe, lit is seen.
 */
export function farrowingFixtures(): Fixture[] {
  const fixtures: Fixture[] = [];
  for (const z of BATTEN_Z) {
    fixtures.push({
      kind: 'batten',
      // 0.35 m below the ceiling, not level with the fitting.
      //
      // A real batten has a body above the tube and throws its light DOWN; a
      // point light at ceiling height throws it in every direction, and the
      // first build put a large soft ellipse of glow on the ceiling above each
      // fitting that read unmistakably as a lamp in a game engine. Point lights
      // here cast no shadows (see lighting.ts), so the fitting cannot occlude
      // its own light and the emitter has to be moved instead.
      position: { x: 0, y: HALL.ceiling - 0.35, z },
      length: 1.5,
      intensity: 6.5,
      range: 9,
    });
  }
  return fixtures;
}

/**
 * The machinery you can hear in this hall, and where it is.
 *
 * Spec §8: no score inside the farm, only machinery. Three sources, and the
 * choice of which three is staging rather than decoration:
 *
 * - **The fluorescent buzz sits over the walkway near where she starts**, so the
 *   first sound she moves through is the one Alex confirmed is genuinely
 *   unpleasant (R-003). It is the room being hostile before anything happens.
 * - **The extractor fan is in the far wall**, which gives the hall a direction —
 *   the vanishing point is also where the noise comes from, so the eye and the
 *   ear agree about which way "onward" is.
 * - **The slurry pump is BEYOND the far wall**, quiet and unlocatable. It is the
 *   first hint that the building continues past the only end she can see.
 *
 * The hall hum is not here. It is the building rather than an object in it, so
 * `GameAudio` plays it without a panner.
 */
export function farrowingAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [
    { id: 'fluorescent-buzz', at: { x: 0, y: HALL.ceiling - 0.2, z: -4 }, gain: 0.6 },
    { id: 'extractor-fan', at: { x: 2.4, y: 2.45, z: HALL.halfLength + 0.2 }, gain: 0.8 },
    { id: 'slurry-pump', at: { x: -2.6, y: 0.4, z: HALL.halfLength + 5 }, gain: 0.4 },
  ];
}

/**
 * Ceiling beams and a wall panel joint. Dressing, and it is load-bearing.
 *
 * A hall of four flat planes reads as an untextured box however well it is
 * shaded — there is simply nothing in it for the eye to measure distance
 * against, so the perspective that is the whole staging does not land. Beams on
 * the bay pitch give the ceiling a rhythm that recedes, and they stripe the
 * walls with the key light's shadow for free.
 *
 * They carry no collider. Nothing in this game can reach 2.8 m.
 */
function structure(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'structure';

  const beamMaterial = surface('mineral', CONCRETE_WALL);
  const bandMaterial = surface('metal', STEEL);

  // Instanced: eight identical beams for one draw call, not eight.
  const beamGeometry = new THREE.BoxGeometry((HALL.halfWidth + HALL.wallThickness) * 2, 0.16, 0.1);
  const beam = new THREE.Mesh(beamGeometry, beamMaterial);
  beam.name = 'beam';
  beam.castShadow = true;
  beam.receiveShadow = true;
  beam.position.y = HALL.ceiling - 0.08;
  g.add(
    instanceGroup(
      beam,
      bayCentres().map((z) => ({ x: 0, z })),
      'ceiling-beams',
    ),
  );

  // The joint between the washable lower wall panel and the wall above it. Every
  // surface a pig can reach in a real unit is impermeable to about a metre; the
  // line that leaves is a free horizontal to read the length of the hall by.
  const bandGeometry = new THREE.BoxGeometry(0.03, 0.05, HALL.halfLength * 2);
  for (const side of [-1, 1] as const) {
    const band = new THREE.Mesh(bandGeometry, bandMaterial);
    band.position.set(side * (HALL.halfWidth - 0.015), 1.1, 0);
    band.name = `panel-joint-${side > 0 ? 'r' : 'l'}`;
    band.castShadow = false;
    band.receiveShadow = true;
    g.add(band);
  }

  return g;
}

/**
 * The two sloped lips of the sluice, as convex prisms.
 *
 * A wedge rather than a tilted box, for exactly the reason `testroom.ts` records
 * for its ramp: a tilted slab either leaves a lip at its foot that autostep will
 * not take, or it intersects the floor and the character controller receives
 * contradictory contacts and slides sideways instead of climbing. A convex hull
 * of the real corners has neither problem.
 *
 * Six corners, in a fixed order, shared by the mesh builder and the collider
 * builder so the two cannot drift:
 *   0,1 inner bottom (at the channel bed)   2,3 outer top (at walkway level)
 *   4,5 outer bottom (the buried edge)
 */
export function farrowingChamferCorners(side: -1 | 1): [number, number, number][] {
  const inner = side * HALL.drainHalfWidth;
  const outer = side * (HALL.drainHalfWidth + HALL.drainChamfer);
  const bed = -HALL.drainDepth;
  const L = HALL.halfLength;
  return [
    [inner, bed, -L],
    [inner, bed, L],
    [outer, 0, -L],
    [outer, 0, L],
    [outer, bed, -L],
    [outer, bed, L],
  ];
}

/** Z centres of the bays, near end first. Everything repeated sits on these. */
function bayCentres(): number[] {
  const out: number[] = [];
  for (let z = -HALL.halfLength + BAY_PITCH / 2; z < HALL.halfLength; z += BAY_PITCH) {
    out.push(z);
  }
  return out;
}

/** Where a partition stands: one per boundary BETWEEN two bays, both sides. */
export function penPartitionLines(): { side: -1 | 1; z: number }[] {
  return ([-1, 1] as const).flatMap((side) =>
    bayCentres().slice(0, -1).map((z) => ({ side, z: z + BAY_PITCH / 2 })),
  );
}

function assetOrThrow(id: string): THREE.Group {
  const asset = getAsset(id);
  if (!asset) throw new Error(`farrowing hall: asset "${id}" is not registered`);
  return asset.create();
}

/**
 * Dress the shell with the kit.
 *
 * Every repeated piece goes through `instanceGroup` (D-024), which also merges
 * each source's meshes by surface — the farrowing crate alone is fifty named
 * pipes, and placed sixteen times unmerged it would cost more draw calls than
 * spec §1 budgets for the entire frame.
 *
 * Layout, and why: the crates run **across** the hall rather than along it, so
 * the player walking the walkway passes a rhythm of cage ends rather than one
 * continuous fence. That rhythm is the composition in plate 1, and it is also
 * what will make patrol timing legible once there is anybody to hide from.
 */
export function dressFarrowingHall(): THREE.Group {
  const dressing = new THREE.Group();
  dressing.name = 'farrowing-dressing';

  const bays = bayCentres();
  const penInner = HALL.walkway + 0.12;

  // Slatted floor over the whole pen area, both sides. Two tiles deep, which
  // reaches 3.22 m of the 3.6 m to the wall — the strip behind is under the
  // crates and against the wall, and no camera in this game is ever high enough
  // to see it.
  const slatPlacements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of bays) {
      for (const dz of [-0.6, 0.6]) {
        for (const dx of [0.6, 1.8]) {
          slatPlacements.push({ x: side * (penInner + dx), z: z + dz });
        }
      }
    }
  }
  dressing.add(instanceGroup(assetOrThrow('slat-floor'), slatPlacements, 'slat-floors'));

  // The crates. Turned a quarter turn so their 2.1 m length runs into the pen,
  // and mirrored side to side so both rows face the walkway.
  const cratePlacements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of bays) {
      if (penGateMouths().some((mouth) => mouth.side === side && mouth.z === z)) continue;
      cratePlacements.push({
        x: side * (penInner + 1.15),
        y: SLAT_HEIGHT,
        z,
        rotY: (side * Math.PI) / 2,
      });
    }
  }
  dressing.add(instanceGroup(assetOrThrow('farrowing-crate'), cratePlacements, 'crates'));

  const accessCratePlacements: Placement[] = penGateMouths().map((mouth) => ({
    x: mouth.side * (penInner + 1.15),
    y: SLAT_HEIGHT,
    z: mouth.z,
    rotY: (mouth.side * Math.PI) / 2,
  }));
  dressing.add(instanceGroup(farrowingCrateGateAccess(), accessCratePlacements, 'gate-access-crates'));

  // Pen rails standing out of the kerbs, one per unbroken bay per side. The
  // weak gate's bay receives two exact spans below: leaving its normal 2.4 m
  // instance here would dress a rail straight across the declared opening.
  const railPlacements: Placement[] = bays.flatMap((z) =>
    ([-1, 1] as const)
      .filter((side) => !penGateMouths().some((mouth) => mouth.side === side && mouth.z === z))
      .map((side) => ({ x: side * (HALL.walkway + 0.06), z })),
  );
  dressing.add(instanceGroup(assetOrThrow('pen-rail'), railPlacements, 'pen-rails'));

  const gateRailSegments = new THREE.Group();
  gateRailSegments.name = 'pen-rail-gate-segments';
  for (const [index, mouth] of penGateMouths().entries()) {
    const bayFrom = mouth.z - BAY_PITCH / 2;
    const bayTo = mouth.z + BAY_PITCH / 2;
    for (const [part, from, to] of [
      ['near', bayFrom, mouth.z - mouth.width / 2],
      ['far', mouth.z + mouth.width / 2, bayTo],
    ] as const) {
      if (to <= from) continue;
      gateRailSegments.add(instanceGroup(
        penRailSpan(to - from),
        [{ x: mouth.side * (HALL.walkway + 0.06), z: (from + to) / 2 }],
        `pen-rail-gate-${index}-${part}`,
      ));
    }
  }
  dressing.add(gateRailSegments);

  // The asset is authored along local Z; the quarter turn lays it across the
  // pen. It is symmetric, so the sign of the turn is immaterial.
  const partitionPlacements: Placement[] = penPartitionLines().map(({ side, z }) => ({
    x: side * 2.34,
    z,
    rotY: Math.PI / 2,
  }));
  dressing.add(instanceGroup(assetOrThrow('pen-partition'), partitionPlacements, 'pen-partitions'));

  const gatePlacements: Placement[] = penGateMouths().map((mouth) => {
    const hinge = {
      x: mouth.side * (HALL.walkway + 0.06),
      z: mouth.z + mouth.swing * mouth.width / 2,
    };
    const leaf = penGateLeaf(hinge, mouth.swing, 0);
    // Placed at the leaf's CENTRE, not at its hinge — the same `pos` and `rotY`
    // the collider two hundred lines up is built from. Not "derived from the
    // same function": the identical three numbers. See `penGateLeaf`.
    return { x: leaf.pos.x, z: leaf.pos.z, rotY: leaf.rotY };
  });
  dressing.add(instanceGroup(assetOrThrow('pen-gate'), gatePlacements, 'pen-gates'));

  // Troughs against the walkway side of each pen, where a person can reach them
  // without going in. Everything in this building is arranged for the people.
  const troughPlacements: Placement[] = bays.flatMap((z) =>
    ([-1, 1] as const).map((side) => ({ x: side * (penInner + 0.35), y: SLAT_HEIGHT, z })),
  );
  dressing.add(instanceGroup(assetOrThrow('feed-trough'), troughPlacements, 'troughs'));

  // The battens. Authored standing on the floor like every other asset, so they
  // are turned over to be mounted — see Placement.rotX.
  const tubePlacements: Placement[] = BATTEN_Z.map((z) => ({
    x: 0,
    y: HALL.ceiling,
    z,
    rotX: Math.PI,
  }));
  dressing.add(instanceGroup(assetOrThrow('ceiling-tube'), tubePlacements, 'battens'));

  // One fan, high in the far wall, facing back down the hall. It is where the
  // noise comes from too (see farrowingAmbience) — the eye and the ear agree
  // about which way onward is.
  dressing.add(
    instanceGroup(
      assetOrThrow('extractor-fan'),
      [{ x: 2.4, y: 2.1, z: HALL.halfLength - 0.02, rotY: Math.PI }],
      'extractor',
    ),
  );

  return dressing;
}

/**
 * Build the hall shell.
 *
 * Shell only: walls, floor, drain, kerbs, pipe runs and the light fittings. The
 * crates, rails, troughs and slats are kit assets from `src/models/env/` and are
 * placed by `dressFarrowingHall`, so the shell can be built and looked at before
 * a single kit piece exists — which is how this room was developed.
 */
export function createFarrowingHall(): THREE.Group {
  const room = new THREE.Group();
  room.name = 'farrowing-hall';

  room.add(solidMeshes(farrowingSolids(), 'shell'));

  // The sluice's sloped lips. Winding is counter-clockwise seen from OUTSIDE the
  // solid; getting it wrong makes the slope see-through from above, which is
  // where the player is, and it reads as a hole rather than as a mistake.
  for (const side of [-1, 1] as const) {
    const c = farrowingChamferCorners(side);
    const v: number[] = [];
    const tri = (a: number, b: number, d: number): void => {
      const pa = c[a];
      const pb = c[b];
      const pd = c[d];
      if (!pa || !pb || !pd) return;
      // The two lips are mirror images, so one winding order cannot serve both.
      if (side > 0) v.push(...pa, ...pb, ...pd);
      else v.push(...pa, ...pd, ...pb);
    };
    tri(0, 1, 2); tri(1, 3, 2);   // the slope itself
    tri(2, 3, 4); tri(3, 5, 4);   // the outer vertical face, buried in the slab
    tri(0, 2, 4);                 // the two ends
    tri(1, 4, 5);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, surface('mineral', CONCRETE_FLOOR));
    mesh.name = `drain-lip-${side > 0 ? 'r' : 'l'}`;
    mesh.receiveShadow = true;
    room.add(mesh);
  }

  room.add(structure());
  room.add(dressFarrowingHall());
  // The walkway door, live rather than merged into the dressing: its leaf
  // turns. Its handle is at 1.05 m, three times her height, which is the
  // point of building it at a real size (D-012).
  room.add(createWalkwayDoor());

  return room;
}
